import { useEffect, useMemo, useState } from 'react'
import { BellRing, MoonStar, RotateCcw } from 'lucide-react'
import { Select, apiGet, adminMutate } from './admin/primitives.js'
import { Switch, SwitchRow } from './admin/form.js'
import { useSaveBar } from './shell/editing.js'
import {
  CATS, CAT_LABEL, CHOIX_LABEL, CH_LABEL, aDesChoix, avecCategorie, avecType, brouillonDepuis, categoriesDu, choixDuType,
  corpsDuPut, memeBrouillon, noteDuReglage, reglageDeCategorie, reglageDuType, reglagesParCategorie, resumeParCategorie,
  type Brouillon, type Cat, type CellPrefs, type Ch, type ChoixType, type FamilleInfo, type PolitiqueLigne, type TypeInfo,
} from './notification-prefs-model.js'
import './notifications.css'

/**
 * MES NOTIFICATIONS — section du profil.
 *
 * Rangées par FAMILLE puis par MODULE, et seulement les modules auxquels la
 * personne a accès : le serveur ne renvoie que les types dont elle détient le
 * droit de réception (moteur de permissions). Une ligne par type, un
 * interrupteur par canal :
 *
 *   Dans Gestion · E-mail · Push (à venir avec l'application mobile)
 *
 * Sous les types, les réglages « par défaut » par catégorie : ils décident
 * pour toute notification que la personne n'a pas réglée une par une. Toute
 * bascule se voit IMMÉDIATEMENT sur les lignes qui en dépendent
 * (`notification-prefs-model.ts`), avant l'enregistrement par la barre
 * d'enregistrement commune.
 *
 * La politique de l'organisation prime : un canal obligatoire reste allumé et
 * verrouillé, un canal coupé reste éteint — c'est écrit sous l'interrupteur.
 *
 * Un type qui part sous PLUSIEURS catégories (les règles d'alerte, l'écart à
 * la finalisation, la relève, le récapitulatif) n'a pas d'interrupteur : un
 * choix « Par catégorie / Activé / Coupé », et sa note dit ce que chaque
 * catégorie donnera (`resumeParCategorie`). Un interrupteur résolu à la seule
 * catégorie par défaut mentait sur les alertes critiques (revue du 08/10/2026).
 */

interface Reponse {
  defaults: Record<Cat, Record<Ch, boolean>>
  prefs: (Partial<Record<Cat, CellPrefs>> & { types?: Record<string, CellPrefs> }) | null
  quiet_hours: Brouillon['quiet'] | null
  org_policy: PolitiqueLigne[]
  groups?: FamilleInfo[]
  push_available?: boolean
}

const CANAUX_ACTIFS: Ch[] = ['IN_APP', 'EMAIL']

/** Le choix d'un type multi-catégories : trois issues, la note dit l'effet par catégorie. */
function CelluleChoix(props: {
  canal: Ch
  choix: ChoixType
  note: string
  ariaLabel: string
  onChange: (v: ChoixType) => void
}) {
  return (
    <span role="cell" className="nt-cell">
      <span className="nt-cell__name" aria-hidden="true">{CH_LABEL[props.canal]}</span>
      <Select
        value={props.choix}
        options={(['SELON', 'ACTIVE', 'COUPE'] as const).map((c) => ({ value: c, label: CHOIX_LABEL[c] }))}
        onChange={(v) => props.onChange(v as ChoixType)}
        ariaLabel={props.ariaLabel}
        compact
      />
      <span className="nt-cell__note">{props.note}</span>
    </span>
  )
}

function Cellule(props: {
  canal: Ch
  on: boolean
  disabled: boolean
  note: string | null
  ariaLabel: string
  onChange: (v: boolean) => void
}) {
  return (
    <span role="cell" className="nt-cell">
      <span className="nt-cell__name" aria-hidden="true">{CH_LABEL[props.canal]}</span>
      <Switch checked={props.on} disabled={props.disabled} onChange={props.onChange} ariaLabel={props.ariaLabel} />
      {props.note ? <span className="nt-cell__note">{props.note}</span> : null}
    </span>
  )
}

/** Le Push : dans le modèle, sans interrupteur — l'en-tête dit pourquoi, la cellule le rappelle. */
function CelluleAVenir() {
  return (
    <span role="cell" className="nt-cell nt-cell--soon" aria-label="Push : à venir avec l’application mobile">
      <span className="nt-cell__name" aria-hidden="true">{CH_LABEL.PUSH}</span>
      <span className="nt-cell__note">À venir</span>
    </span>
  )
}

function EnTete({ premier }: { premier: string }) {
  return (
    <div className="nt-row nt-row--head" role="row">
      <span role="columnheader">{premier}</span>
      {CANAUX_ACTIFS.map((ch) => <span key={ch} role="columnheader">{CH_LABEL[ch]}</span>)}
      <span role="columnheader" className="nt-head-soon">
        {CH_LABEL.PUSH}<span className="nt-head-soon__note">à venir avec l’application mobile</span>
      </span>
    </div>
  )
}

export function NotificationPrefsSection() {
  const [rep, setRep] = useState<Reponse | null>(null)
  const [server, setServer] = useState<Brouillon | null>(null)
  const [draft, setDraft] = useState<Brouillon | null>(null)

  useEffect(() => {
    apiGet<Reponse>('/api/v1/me/notification-preferences').then((j) => {
      if (!j || !j.defaults) return
      const b = brouillonDepuis(j.prefs, j.quiet_hours)
      setRep(j)
      setServer(b)
      setDraft(structuredClone(b))
    }).catch(() => undefined)
  }, [])

  const dirty = useMemo(() => !memeBrouillon(server, draft), [server, draft])
  useSaveBar({
    dirty,
    onSave: async () => {
      if (!draft) return 'Rien à enregistrer.'
      const r = await adminMutate<{ error?: string; message?: string }>('PUT', '/api/v1/me/notification-preferences', corpsDuPut(draft))
      if (r.status < 300) { setServer(structuredClone(draft)); return true }
      return r.data.message ?? 'L’enregistrement a échoué.'
    },
    onCancel: () => setDraft(server ? structuredClone(server) : null),
    successMessage: 'Notifications enregistrées — sur tous vos appareils.',
  })

  if (!rep || !draft) return null
  const groupes = rep.groups ?? []
  const policy = rep.org_policy

  const ligneType = (t: TypeInfo) => (
    <div key={t.key} className="nt-row" role="row">
      <span role="cell" className="nt-type">
        <span className="nt-type__head">
          <span className="nt-type__label">{t.label}</span>
          <span className={`lc-pill nt-cat nt-cat--${t.default_category.toLowerCase()}`}>{t.category_label}</span>
        </span>
        <span className="nt-type__desc">{t.description}</span>
      </span>
      {CANAUX_ACTIFS.map((ch) => {
        const contexte = { t, defaults: rep.defaults, policy, reference: server }
        if (categoriesDu(t).length > 1 && t.allowed_channels.includes(ch)) {
          const parCat = reglagesParCategorie(t, ch, draft, rep.defaults, policy)
            .map((x) => ({ categorie: x.categorie, on: x.reglage.on, verrou: x.reglage.verrou }))
          return (
            <CelluleChoix key={ch} canal={ch} choix={choixDuType(draft, t.key, ch)}
              note={resumeParCategorie(parCat)}
              ariaLabel={`${t.label} — ${CH_LABEL[ch]}`}
              onChange={(c) => setDraft((d) => d ? avecType(d, t.key, ch, c === 'SELON' ? null : c === 'ACTIVE', contexte) : d)} />
          )
        }
        const r = reglageDuType(t, ch, draft, rep.defaults, policy)
        return (
          <Cellule key={ch} canal={ch} on={r.on} disabled={r.verrou !== null || !t.allowed_channels.includes(ch)}
            note={noteDuReglage(r, t.default_category)}
            ariaLabel={`${t.label} — ${CH_LABEL[ch]}${r.verrou === 'MANDATORY' ? ', imposé par l’organisation' : r.verrou === 'DISABLED' ? ', coupé par l’organisation' : ''}`}
            onChange={(v) => setDraft((d) => d ? avecType(d, t.key, ch, v, contexte) : d)} />
        )
      })}
      <CelluleAVenir />
    </div>
  )

  return (
    <section className="lc-panel gd-panel nt-panel" aria-labelledby="pf-notif">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="pf-notif">
          <BellRing size={16} aria-hidden="true" className="nt-icon" />Notifications
        </h2>
      </header>
      <div className="lc-panel__body nt-body">
        <p className="nt-intro">
          Vous recevez les notifications des modules auxquels vous avez accès. Choisissez,
          ligne par ligne, où chacune vous arrive. Quand l’organisation impose ou coupe un
          canal, c’est écrit sous l’interrupteur. Le Push arrivera avec l’application mobile.
        </p>

        {groupes.length === 0 ? (
          <p className="nt-intro">Aucun de vos modules n’envoie de notification réglable pour le moment.</p>
        ) : groupes.map((f) => (
          <div key={f.family_id} className="nt-family">
            <h3 className="nt-family__title">{f.family_label}</h3>
            {f.modules.map((m) => (
              <div key={m.module_id ?? 'famille'} className="nt-module">
                <h4 className="nt-module__title">{m.module_label}</h4>
                <div className="nt-grid" role="table" aria-label={`Notifications — ${f.family_label}, ${m.module_label}`}>
                  <EnTete premier="Notification" />
                  {m.types.map(ligneType)}
                </div>
              </div>
            ))}
          </div>
        ))}

        <div className="nt-family">
          <div className="nt-defaults__head">
            <h3 className="nt-family__title">Par défaut, par catégorie</h3>
            {aDesChoix(draft) ? (
              <button type="button" className="lc-btn lc-btn--ghost"
                onClick={() => setDraft((d) => d ? { ...d, cats: {}, types: {} } : d)}>
                <RotateCcw size={14} aria-hidden="true" /> Revenir aux réglages proposés
              </button>
            ) : null}
          </div>
          <p className="nt-intro">
            Pour une notification que vous n’avez pas réglée ligne par ligne, le choix fait ici
            pour sa catégorie l’emporte sur le réglage proposé par Gestion.
          </p>
          <div className="nt-grid" role="table" aria-label="Réglages par défaut, par catégorie">
            <EnTete premier="Catégorie" />
            {CATS.map((c) => (
              <div key={c} className="nt-row" role="row">
                <span role="cell" className="nt-type">
                  <span className="nt-type__label">{CAT_LABEL[c]}</span>
                </span>
                {CANAUX_ACTIFS.map((ch) => {
                  const r = reglageDeCategorie(c, ch, draft, rep.defaults, policy)
                  return (
                    <Cellule key={ch} canal={ch} on={r.on} disabled={r.verrou !== null}
                      note={r.verrou === 'MANDATORY' ? 'Imposé par l’organisation' : r.verrou === 'DISABLED' ? 'Coupé par l’organisation' : null}
                      ariaLabel={`${CAT_LABEL[c]} — ${CH_LABEL[ch]}`}
                      onChange={(v) => setDraft((d) => d ? avecCategorie(d, c, ch, v, { defaults: rep.defaults, reference: server }) : d)} />
                  )
                })}
                <CelluleAVenir />
              </div>
            ))}
          </div>
        </div>

        <div className="nt-family">
          <h3 className="nt-family__title">
            <MoonStar size={14} aria-hidden="true" className="nt-icon" />Heures silencieuses
          </h3>
          <SwitchRow
            checked={draft.quiet.enabled}
            onChange={(v) => setDraft((d) => d ? { ...d, quiet: { ...d.quiet, enabled: v } } : d)}
            title="Ne pas m’envoyer d’e-mail pendant mes heures calmes"
            desc="Personnelles, à l’heure de Paris : aucune plage « nuit » n’est imposée. Ce qui arrive dans Gestion continue d’arriver." />
          {draft.quiet.enabled ? (
            <div className="nt-quiet">
              <label className="lc-field">
                <span className="lc-label">De</span>
                <input type="time" className="lc-input" value={draft.quiet.start}
                  onChange={(e) => setDraft((d) => d ? { ...d, quiet: { ...d.quiet, start: e.target.value } } : d)} />
              </label>
              <label className="lc-field">
                <span className="lc-label">À</span>
                <input type="time" className="lc-input" value={draft.quiet.end}
                  onChange={(e) => setDraft((d) => d ? { ...d, quiet: { ...d.quiet, end: e.target.value } } : d)} />
              </label>
              <SwitchRow
                checked={draft.quiet.allow_critical}
                onChange={(v) => setDraft((d) => d ? { ...d, quiet: { ...d.quiet, allow_critical: v } } : d)}
                title="Laisser passer le critique"
                desc="Un guichet ouvert sur la mauvaise soirée, un réseau injoignable : l’e-mail part quand même." />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
